"use client";

import { Footer } from "@/components/footer";
import { Navbar } from "@/components/navbar";
import { Card } from "@heroui/react";

export default function DpaPage() {
  return (
    <div className="marketing-surfaces min-h-screen bg-surface-base text-text-primary flex flex-col">
      <Navbar />

      <main className="heroui flex-1 px-5 sm:px-8 py-12 max-w-3xl mx-auto w-full">
        <h1 className="text-3xl font-semibold tracking-tight mb-2">Data Processing Addendum</h1>
        <p className="text-base text-text-quaternary mb-8">Last updated: June 11, 2026</p>

        <Card
          variant="default"
          className="mb-10 gap-0! rounded-2xl border border-brand/30 bg-brand/6! p-6 shadow-none"
        >
          <p className="text-sm font-semibold uppercase tracking-wide text-brand mb-2">
            In plain English
          </p>
          <div className="space-y-3 text-base text-text-secondary leading-relaxed">
            <p>
              When you use Encrata, we may process personal data on your behalf, including
              information about your customers, users, employees, or other people you look up
              through our services.
            </p>
            <p>
              You control the data you send to Encrata. We process it only to provide, secure,
              support, and improve the services you use. We do not sell it, we do not use it for
              unrelated purposes, and we protect it with appropriate technical and organizational
              safeguards.
            </p>
            <p>
              Where data protection laws apply, Encrata acts as your processor or service
              provider. That means we handle personal data according to your instructions, use{" "}
              <a href="/legal/subprocessors" className="text-brand hover:underline">
                trusted subprocessors
              </a>{" "}
              only where necessary, and take reasonable steps to help you meet your privacy and
              security obligations.
            </p>
            <p>
              Your customers&apos; data deserves the same care as your own. That is the point of
              this addendum.
            </p>
            <p>
              If you have questions,{" "}
              <a href="/contact" className="text-brand hover:underline">contact us</a>.
            </p>
          </div>
        </Card>

        <div className="space-y-8 text-base text-text-secondary leading-relaxed">
          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">1. Scope</h2>
            <p>
              This Data Processing Addendum (&quot;DPA&quot;) forms part of the agreement between Encrata
              (&quot;Processor&quot;) and the customer (&quot;Controller&quot;) and applies where Encrata processes
              personal data on behalf of the customer in connection with the Service, as required
              by the GDPR, UK GDPR, and CCPA.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">2. Roles and Responsibilities</h2>
            <p>
              The customer acts as the data controller and determines the purposes and means of
              processing. Encrata acts as a data processor and processes personal data only on
              documented instructions from the customer, including with regard to international
              transfers, unless required otherwise by law.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">3. Nature of Processing</h2>
            <ul className="list-disc list-outside ps-5 space-y-1 text-text-tertiary">
              <li><span className="text-text-secondary">Subject matter:</span> provision of OSINT and data enrichment APIs</li>
              <li><span className="text-text-secondary">Duration:</span> the term of the agreement</li>
              <li><span className="text-text-secondary">Categories of data:</span> identifiers submitted for lookup (emails, phone numbers, domains, IPs, images) and account data</li>
              <li><span className="text-text-secondary">Data subjects:</span> the customer&apos;s end users and lookup subjects</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">4. Confidentiality and Security</h2>
            <p>
              Encrata ensures persons authorized to process personal data are bound by
              confidentiality obligations. We implement appropriate technical and organizational
              measures, including encryption in transit (TLS 1.2+), encryption at rest, access
              controls, audit logging, and periodic security reviews.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">5. Subprocessors</h2>
            <p>
              The customer provides general authorization for Encrata to engage subprocessors
              listed at{" "}
              <a href="/legal/subprocessors" className="text-brand hover:underline">
                encrata.com/legal/subprocessors
              </a>
              . We will provide notice of changes and impose data protection obligations on
              subprocessors equivalent to those in this DPA.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">6. International Transfers</h2>
            <p>
              Where personal data is transferred outside the EEA or UK, Encrata relies on the
              European Commission&apos;s Standard Contractual Clauses (SCCs) or other valid transfer
              mechanisms.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">7. Data Subject Rights</h2>
            <p>
              Taking into account the nature of processing, Encrata will assist the customer with
              appropriate technical and organizational measures to respond to data subject
              requests (access, rectification, erasure, restriction, portability, objection).
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">8. Breach Notification</h2>
            <p>
              Encrata will notify the customer without undue delay, and in any event within 72
              hours, after becoming aware of a personal data breach affecting the customer&apos;s
              data, providing sufficient information to meet the customer&apos;s notification
              obligations.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">9. Deletion and Return</h2>
            <p>
              Upon termination of the agreement, Encrata will delete or return all personal data
              processed on behalf of the customer within 30 days, unless retention is required by
              law.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">10. Audits</h2>
            <p>
              Encrata will make available information necessary to demonstrate compliance with
              this DPA and allow for audits, including inspections, conducted by the customer or
              an auditor mandated by the customer, subject to reasonable notice and
              confidentiality.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">11. Contact</h2>
            <p>
              To execute a signed copy of this DPA or for questions, contact{" "}
              <a href="mailto:privacy@encrata.com" className="text-brand hover:underline">
                privacy@encrata.com
              </a>
              .
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
